import { AuthContext } from '@repo-level/core-front';
import { useContext, useMemo } from 'react';
import { MISSIONS, QuestMissionPrizeType, QuestMissionType } from '../helpers';
import moment from 'moment';

import missionFutureSrc from '../../../assets/images/quest/mission-future.webp';
import missionFailedSrc from '../../../assets/images/quest/mission-failed.webp';
import missionAvailableSrc from '../../../assets/images/quest/mission-available.webp';
import { groupMemberBH, groupMemberCommonVIP } from 'helpers/user';

export enum EMissionMode {
  INACTIVE = 'INACTIVE',
  FAILED = 'FAILED',
  AVAILABLE = 'AVAILABLE',
  COMPLETE = 'COMPLETE',
}

enum EMissionStatusGroup {
  VIP = 'VIP',
  CASUAL = 'Casual',
  DAILY = 'Mission',
}

export interface IQuestMission {
  mode: EMissionMode;
  foregroundSrc: string | undefined;
  index: number;
  date: string;
  type: QuestMissionType;
  prizeType: QuestMissionPrizeType;
  missionUrl?: string;
  completeImg: string;
  title: string;
  mission: string;
  vipMission: string;
  prizeUrl?: string;
  isSpecialMission?: boolean;
  isToday: boolean;
}

export const useQuestPromo = () => {
  const { user, isAuthenticated } = useContext(AuthContext);

  const isVIP = groupMemberCommonVIP(user);
  const isBonusHunter = groupMemberBH(user);

  const isVipOrBonusHunter = isVIP || isBonusHunter;

  const missions: IQuestMission[] = useMemo(() => {
    return MISSIONS.map((mission, index) => {
      const today = moment().utc().format('YYYY-MM-DD');

      const isToday = mission.date === today;
      const isFuture = mission.date > today;
      const isComplete = !!user?.statuses?.find(({ id }) => {
        const subGroup = isVipOrBonusHunter
          ? EMissionStatusGroup.VIP
          : EMissionStatusGroup.CASUAL;

        return id === `${EMissionStatusGroup.DAILY}_${index + 1}_${subGroup}`;
      });

      const mode = (() => {
        if (!isAuthenticated) {
          return EMissionMode.INACTIVE;
        }

        if (isComplete) {
          return EMissionMode.COMPLETE;
        }

        if (isFuture) {
          return EMissionMode.INACTIVE;
        }

        if (isToday) {
          return EMissionMode.AVAILABLE;
        }

        return EMissionMode.FAILED;
      })();

      const foregroundSrc = (() => {
        if (mode === EMissionMode.INACTIVE) {
          return missionFutureSrc;
        } else if (mode === EMissionMode.FAILED) {
          return missionFailedSrc;
        } else if (mode === EMissionMode.AVAILABLE) {
          return missionAvailableSrc;
        }
      })();

      return { ...mission, mode, foregroundSrc, index, isToday };
    });
  }, [isAuthenticated, isVipOrBonusHunter, user?.statuses]);

  const currentMission = missions.find(({ isToday }) => isToday);

  return { missions, currentMission };
};
